import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  useGetChapterUpgradeApplicationDetailsQuery,
  useChapterAcceptUpgradeApplicationMutation,
  useChapterRejectUpgradeApplicationMutation,
} from "../api/chapterApi";
import {
  ArrowLeft,
  Loader2,
  AlertCircle,
  ShieldCheck,
  ArrowUpCircle,
  BookOpen,
  GraduationCap,
  FileText,
  Users,
} from "lucide-react";

const STORAGE_BASE = "https://api.niqsng.org/storage";
const storageUrl = (p?: string | null) =>
  !p ? null : String(p).startsWith("http") ? String(p) : `${STORAGE_BASE}/${String(p).replace(/^\//, "")}`;
const certOf = (r: any): string | null =>
  r?.cert_file ?? r?.certificate_path ?? r?.cert_url ?? r?.file ?? null;

function safeMsg(x: any) {
  return String(x?.data?.message || x?.message || x?.error || "Something went wrong");
}
function fmtDate(iso?: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { dateStyle: "medium" });
}
function subjectsText(subjects: any): string {
  if (!subjects || typeof subjects !== "object") return "—";
  return Object.entries(subjects).map(([k, v]) => `${k}: ${v}`).join(", ");
}

function Section({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
      <div className="flex items-center gap-2 px-5 py-3 border-b border-gray-50 bg-gray-50/50">
        <span className="text-gray-400">{icon}</span>
        <h3 className="text-xs font-black uppercase tracking-widest text-gray-700">{title}</h3>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}
function Field({ label, value }: { label: string; value?: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{label}</div>
      <div className="text-sm text-gray-800 mt-0.5">{value ?? "—"}</div>
    </div>
  );
}
function Table({ heads, rows }: { heads: string[]; rows: React.ReactNode[][] }) {
  if (rows.length === 0) return <p className="text-sm text-gray-400">None provided.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm min-w-[420px]">
        <thead>
          <tr className="text-left border-b border-gray-100">
            {heads.map((h) => (
              <th key={h} className="py-2 pr-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-gray-50">
              {r.map((c, j) => (
                <td key={j} className="py-2.5 pr-4 align-top">{c}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ChapterUpgradeApplicationDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { data: app, isLoading, isError, refetch } =
    useGetChapterUpgradeApplicationDetailsQuery(id, { skip: !id });

  const [accept, acceptState] = useChapterAcceptUpgradeApplicationMutation();
  const [reject, rejectState] = useChapterRejectUpgradeApplicationMutation();
  const [mode, setMode] = useState<"endorse" | "reject" | null>(null);
  const [text, setText] = useState("");
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto p-6 py-24 flex flex-col items-center gap-3 text-gray-400">
        <Loader2 className="w-7 h-7 animate-spin" />
        <p className="text-sm">Loading application…</p>
      </div>
    );
  }
  if (isError || !app) {
    return (
      <div className="max-w-4xl mx-auto p-6 py-20 text-center space-y-3">
        <AlertCircle className="w-8 h-8 text-red-400 mx-auto" />
        <p className="text-sm text-red-500">Could not load this application.</p>
        <button onClick={() => refetch()} className="px-4 py-2 rounded-lg border text-sm hover:bg-gray-50">
          Retry
        </button>
      </div>
    );
  }

  const canAct =
    ["submitted", "awaiting_endorsements", "under_review", "returned"].includes(app.status) &&
    !app.chapter_accepted_at;
  const grade =
    [app.from_category?.name, app.to_category?.name].filter(Boolean).join(" → ") || "Membership upgrade";

  const submit = async () => {
    setMsg(null);
    try {
      if (mode === "endorse") {
        if (!text.trim()) { setMsg({ ok: false, text: "A comment is required to endorse." }); return; }
        await accept({ id, comment: text.trim() }).unwrap();
      } else {
        await reject({ id, reason: text.trim() }).unwrap();
      }
      setMode(null);
      setText("");
      navigate("/chapter/my-upgrade-applications");
    } catch (e) {
      setMsg({ ok: false, text: safeMsg(e) });
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-5">
      <div className="flex items-center justify-between gap-4">
        <Link to="/chapter/my-upgrade-applications" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900">
          <ArrowLeft className="w-4 h-4" /> Back to upgrade applications
        </Link>
        {app.chapter_accepted_at && (
          <span className="inline-flex items-center gap-1 text-xs font-black text-green-600">
            <ShieldCheck className="w-4 h-4" /> Endorsed by chapter
          </span>
        )}
      </div>

      <div className="flex items-center gap-2 text-gray-900">
        <ArrowUpCircle className="w-5 h-5 text-indigo-600" />
        <h1 className="text-2xl font-black tracking-tight capitalize">
          {app.surname} {app.other_names}
        </h1>
        <span className="text-sm text-gray-400 font-medium">({app.title})</span>
      </div>

      {msg && (
        <div className={`p-3 rounded-xl text-sm font-bold ${msg.ok ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
          {msg.text}
        </div>
      )}

      {/* Action bar */}
      {canAct && (
        <div className="flex gap-2">
          <button
            onClick={() => { setMode("reject"); setText(""); setMsg(null); }}
            className="px-4 py-2.5 bg-white border border-red-100 text-red-600 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-red-50"
          >
            Reject
          </button>
          <button
            onClick={() => { setMode("endorse"); setText(""); setMsg(null); }}
            className="px-4 py-2.5 bg-green-600 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-green-700"
          >
            Endorse
          </button>
        </div>
      )}

      <Section icon={<FileText className="w-4 h-4" />} title="Applicant details">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Upgrade" value={grade} />
          <Field label="Route" value={app.route ? <span className="capitalize">{app.route}</span> : "—"} />
          <Field label="Status" value={<span className="capitalize">{String(app.status).replace("_", " ")}</span>} />
          <Field label="Membership No." value={app.user?.member?.membership_no} />
          <Field label="Email" value={app.email || app.user?.email} />
          <Field label="Phone" value={app.phone} />
          <Field label="Gender" value={app.gender} />
          <Field label="Date of birth" value={app.date_of_birth ? fmtDate(app.date_of_birth) : "—"} />
          <Field label="Nationality" value={app.nationality} />
          <Field label="Chapter" value={app.chapter?.name ?? app.chapter_other} />
          <Field label="Residential address" value={app.residential_address} />
          <Field label="Postal address" value={app.postal_address} />
        </div>
      </Section>

      <Section icon={<FileText className="w-4 h-4" />} title="Academic qualifications">
        <Table
          heads={["Institution", "Qualification", "Year", "Certificate"]}
          rows={(app.qualifications ?? []).map((q: any) => [
            q.institution, q.qualification, q.year,
            storageUrl(certOf(q)) ? <a className="text-indigo-600 hover:underline" href={storageUrl(certOf(q))!} target="_blank" rel="noreferrer">View</a> : "—",
          ])}
        />
      </Section>

      <Section icon={<FileText className="w-4 h-4" />} title="O'level results">
        <Table
          heads={["Exam", "Year", "Exam No.", "Subjects", "Result"]}
          rows={(app.olevel_results ?? []).map((r: any) => [
            String(r.exam_type ?? "").toUpperCase(), r.exam_year, r.exam_number, subjectsText(r.subjects),
            storageUrl(r.result_file) ? <a className="text-indigo-600 hover:underline" href={storageUrl(r.result_file)!} target="_blank" rel="noreferrer">View</a> : "—",
          ])}
        />
      </Section>

      {(app.prelim_results ?? []).length > 0 && (
        <Section icon={<FileText className="w-4 h-4" />} title="Prelim / IJMB results">
          <Table
            heads={["Exam", "Year", "Institution", "Exam No.", "Subjects", "Result"]}
            rows={(app.prelim_results ?? []).map((p: any) => [
              p.exam_type === "others" ? (p.other_exam_name || "Others") : String(p.exam_type ?? "").toUpperCase(),
              p.exam_year, p.institution, p.exam_number, subjectsText(p.subjects),
              storageUrl(p.result_file) ? <a className="text-indigo-600 hover:underline" href={storageUrl(p.result_file)!} target="_blank" rel="noreferrer">View</a> : "—",
            ])}
          />
        </Section>
      )}

      <Section icon={<FileText className="w-4 h-4" />} title="Professional memberships">
        <Table
          heads={["Body", "Stage passed", "Certificate"]}
          rows={(app.professional_memberships ?? []).map((m: any) => [
            m.body_name, m.stage_passed,
            storageUrl(m.certificate_path) ? <a className="text-indigo-600 hover:underline" href={storageUrl(m.certificate_path)!} target="_blank" rel="noreferrer">View</a> : "—",
          ])}
        />
      </Section>

      <Section icon={<Users className="w-4 h-4" />} title="Referees">
        <Table
          heads={["Referee", "Membership No.", "Relationship", "Decision"]}
          rows={(app.referees ?? []).map((r: any) => [
            [r.member?.surname, r.member?.othernames].filter(Boolean).join(" ") || r.member_id,
            r.member?.membership_no, r.relationship,
            <span className="capitalize">{r.decision}</span>,
          ])}
        />
      </Section>

      <Section icon={<BookOpen className="w-4 h-4" />} title="Logbooks">
        <Table
          heads={["Diet", "Status", "Created"]}
          rows={(app.logbooks ?? []).map((l: any) => [
            l.diet?.title || "—",
            <span className="capitalize">{String(l.status ?? "").replace("_", " ")}</span>,
            l.created_at ? fmtDate(l.created_at) : "—",
          ])}
        />
      </Section>

      <Section icon={<GraduationCap className="w-4 h-4" />} title="Examinations">
        <Table
          heads={["Exam", "Diet", "Status", "Result"]}
          rows={(app.exam_applications ?? []).map((e: any) => [
            e.exam?.title || e.exam?.name || e.exam?.code || "—",
            e.diet?.name || e.diet?.title || "—",
            <span className="capitalize">{String(e.status ?? "").replace("_", " ")}</span>,
            e.result?.recommendation || e.result?.overall_status || e.result?.status || e.result?.grade || "—",
          ])}
        />
      </Section>

      {/* Endorse / Reject modal */}
      {mode && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 space-y-5">
            <h2 className="font-black text-gray-900 text-lg">
              {mode === "endorse" ? "Endorse application" : "Reject application"}
            </h2>
            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                {mode === "endorse" ? "Chapter comment" : "Reason"}
                {mode === "endorse" && <span className="text-red-500"> *</span>}
              </label>
              <textarea
                className="mt-1 w-full p-4 bg-gray-50 border border-gray-100 rounded-2xl text-sm outline-none focus:ring-2 focus:ring-gray-900/5 min-h-[100px]"
                placeholder={mode === "endorse" ? "Your comment endorsing this candidate…" : "Reason (optional)…"}
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => { setMode(null); setText(""); }}
                className="flex-1 py-3 rounded-xl border border-gray-200 text-[12px] font-black text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={submit}
                disabled={acceptState.isLoading || rejectState.isLoading || (mode === "endorse" && !text.trim())}
                className={`flex-1 py-3 rounded-xl text-white text-[12px] font-black uppercase tracking-wider disabled:opacity-60 ${
                  mode === "endorse" ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"
                }`}
              >
                {acceptState.isLoading || rejectState.isLoading
                  ? "Saving…"
                  : mode === "endorse" ? "Confirm Endorse" : "Confirm Reject"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
